import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tag, Image, Modal, Form, Input, InputNumber, Switch, message } from 'antd';
import { ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { ResourceList } from '@/components/ResourceList';
import {
  listPackages,
  listCategories,
  createPackage,
  type ContentPackage,
  type PackageCategory,
} from '@/api/content';
import { formatCNY, formatDateTime } from '@/utils/format';

const statusColor: Record<string, string> = {
  active: 'green',
  draft: 'gold',
  reviewing: 'blue',
  archived: 'default',
};

export function ContentListPage() {
  const navigate = useNavigate();

  // 当前选中的 app_id (用来拉对应 app 的分类)。初始值 3 (photo-studio)。
  // filters 里的 app_id 用户输入后触发重新拉分类。
  const [currentAppId, setCurrentAppId] = useState<number>(3);
  const [categories, setCategories] = useState<PackageCategory[]>([]);
  const lastFetchedAppId = useRef<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const onCreate = async () => {
    try {
      const v = await form.validateFields();
      setCreating(true);
      const yuan = Number(v.price_yuan ?? 0);
      const pkg = await createPackage({
        app_id: Number(v.app_id),
        code: String(v.code).trim(),
        name: String(v.name).trim(),
        workflow_code: String(v.workflow_code || 'WF_CHARACTER_GEN').trim(),
        cover_url: v.cover_url ? String(v.cover_url).trim() : undefined,
        price_cents: Math.round(yuan * 100),
        config: v.template_swap ? { template_swap: true } : undefined,
      });
      message.success(`已创建 ${pkg.code} — 去详情加底版/标框`);
      setCreateOpen(false);
      form.resetFields();
      navigate(`/content/${pkg.id}`);
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return; // 表单校验
      message.error(e instanceof Error ? e.message : '创建失败');
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (lastFetchedAppId.current === currentAppId) return;
    lastFetchedAppId.current = currentAppId;
    listCategories(currentAppId).then(setCategories).catch(() => setCategories([]));
  }, [currentAppId]);

  const categoryOptions = useMemo(
    () => categories.filter((c) => c.enabled !== false).map((c) => ({ value: c.slug, label: c.name })),
    [categories],
  );

  return (
    <>
    <ResourceList<ContentPackage>
      title="内容套系"
      description="一个套系 = 一组 prompt 变量 + 样片 + 商品定价；编辑触发 package_versions 新版本。"
      rowKey="id"
      pagination="offset"
      actions={[
        {
          label: '新建套系',
          icon: <PlusOutlined />,
          onClick: () => setCreateOpen(true),
        },
        {
          label: '批量导入',
          icon: <ImportOutlined />,
          onClick: () => navigate('/content/bulk-import'),
        },
      ]}
      filters={[
        { key: 'q', label: '关键字', type: 'text', placeholder: 'code 或 name' },
        { key: 'app_id', label: 'App ID', type: 'text', placeholder: '数字 app id' },
        {
          key: 'status',
          label: '状态',
          type: 'select',
          options: [
            { value: 'draft', label: '草稿' },
            { value: 'reviewing', label: '审核中' },
            { value: 'active', label: '活跃' },
            { value: 'archived', label: '已归档' },
          ],
        },
        {
          key: 'category',
          label: '分类',
          type: 'select',
          options: categoryOptions,
          allowClear: true,
        },
      ]}
      fetcher={({ filters, page, pageSize }) => {
        const f = { ...filters } as Record<string, unknown>;
        if (typeof f.app_id === 'string' && f.app_id !== '') {
          const parsed = Number(f.app_id);
          if (!Number.isNaN(parsed)) {
            f.app_id = parsed;
            // 用户切换了 app_id → 拉新 app 的分类
            setCurrentAppId(parsed);
          }
        }
        return listPackages({ ...f, page, page_size: pageSize } as never);
      }}
      columns={[
        { title: 'ID', dataIndex: 'id', width: 70 },
        {
          title: '封面',
          dataIndex: 'cover_url',
          width: 70,
          render: (v) =>
            v ? <Image src={v} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} /> : '-',
        },
        {
          title: '名称',
          dataIndex: 'name',
          render: (_, row) => <Link to={`/content/${row.id}`}>{row.name}</Link>,
        },
        { title: 'App', dataIndex: 'app_id', render: (v) => <Tag color="blue">app#{v}</Tag> },
        {
          title: '工作流',
          dataIndex: 'workflow_code',
          render: (v) => (v ? <Tag>{v}</Tag> : '-'),
        },
        {
          title: '售价',
          dataIndex: 'price_cents',
          align: 'right',
          render: (v) => formatCNY(v),
        },
        {
          title: '消耗钻石',
          dataIndex: 'diamond_cost',
          align: 'right',
          render: (v) => v ?? 0,
        },
        {
          title: '销量',
          dataIndex: 'sales_count',
          align: 'right',
          render: (v) => v ?? 0,
        },
        {
          title: '状态',
          dataIndex: 'status',
          render: (v) => <Tag color={statusColor[v] || 'default'}>{v}</Tag>,
        },
        {
          title: '更新时间',
          dataIndex: 'updated_at',
          render: (v) => formatDateTime(v),
        },
      ]}
    />
    <Modal
      title="新建套系"
      open={createOpen}
      onCancel={() => setCreateOpen(false)}
      onOk={onCreate}
      confirmLoading={creating}
      okText="创建并去详情"
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ app_id: 3, workflow_code: 'WF_CHARACTER_GEN', template_swap: true, price_yuan: 29.9 }}>
        <Form.Item name="app_id" label="App ID" rules={[{ required: true }]}>
          <InputNumber min={1} style={{ width: '100%' }} placeholder="3 = photo-studio" />
        </Form.Item>
        <Form.Item name="code" label="套系 code (唯一)" rules={[{ required: true }]}>
          <Input placeholder="如 my_series_001" />
        </Form.Item>
        <Form.Item name="name" label="套系名称" rules={[{ required: true }]}>
          <Input placeholder="如 春日和风" />
        </Form.Item>
        <Form.Item name="workflow_code" label="工作流" rules={[{ required: true }]}>
          <Input placeholder="换脸套系填 WF_CHARACTER_GEN" />
        </Form.Item>
        <Form.Item name="template_swap" label="模板换脸 (底版+换脸, 创想熊式)" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="price_yuan" label="售价 (元)">
          <InputNumber min={0} step={0.1} style={{ width: '100%' }} addonAfter="元" />
        </Form.Item>
        <Form.Item name="cover_url" label="封面 (OSS key 或 URL, 可选)">
          <Input placeholder="留空可在详情页再传; 模板换脸的底版/标框也在详情页加" />
        </Form.Item>
      </Form>
    </Modal>
    </>
  );
}
